
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Course, EnrolledCourse } from '@/types';
import LearningHeader from '@/components/learning/LearningHeader';
import MyLearningTabContent from '@/components/learning/MyLearningTabContent';
import CoursesTabContent from '@/components/learning/CoursesTabContent';
import NewsTabContent from '@/components/learning/NewsTabContent';
import CertificationsTabContent from '@/components/learning/CertificationsTabContent';

const Learning: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>('my-learning');

  // Parse the URL query parameters to check for a tab parameter
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');
    if (tabParam && ['my-learning', 'courses', 'news', 'certifications'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Handle tab change and update URL
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', value);
    window.history.pushState({}, '', url.toString());
  };

  // Mock data for courses and learning progress
  const mockCourses: Course[] = [
    {
      id: 'course-1',
      title: 'Music Business Foundations',
      description: 'Learn the essentials of the music business industry',
      category: 'Business',
      level: 'intermediate',
      duration: 480, // 8 hours
      instructor: 'John Smith',
      thumbnailUrl: '/placeholder.svg',
      modules: [],
      tags: ['business', 'industry', 'career'],
      popularity: 98,
      rating: 4.7,
      ratingCount: 325,
      xpReward: 500
    },
    {
      id: 'course-2',
      title: 'Advanced Music Production',
      description: 'Take your production skills to the next level',
      category: 'Production',
      level: 'advanced',
      duration: 720, // 12 hours
      instructor: 'Sarah Johnson',
      thumbnailUrl: '/placeholder.svg',
      modules: [],
      tags: ['production', 'mixing', 'mastering'],
      popularity: 95,
      rating: 4.8,
      ratingCount: 412,
      xpReward: 750
    },
    {
      id: 'course-3',
      title: 'Music Marketing Essentials',
      description: 'Learn how to market your music effectively',
      category: 'Marketing',
      level: 'beginner',
      duration: 360, // 6 hours
      instructor: 'Mike Williams',
      thumbnailUrl: '/placeholder.svg',
      modules: [],
      tags: ['marketing', 'promotion', 'social media'],
      popularity: 92,
      rating: 4.5,
      ratingCount: 287,
      xpReward: 450
    }
  ];

  const mockEnrolledCourses: EnrolledCourse[] = [
    {
      courseId: 'course-1',
      enrollmentDate: new Date(2023, 3, 15),
      progress: 65,
      currentModuleId: 'module-5',
      completedModules: ['module-1', 'module-2', 'module-3', 'module-4']
    },
    {
      courseId: 'course-2',
      enrollmentDate: new Date(2023, 5, 20),
      progress: 25,
      currentModuleId: 'module-3',
      completedModules: ['module-1', 'module-2']
    },
    {
      courseId: 'course-3',
      enrollmentDate: new Date(2023, 6, 10),
      progress: 15,
      currentModuleId: 'module-1',
      completedModules: []
    }
  ];

  // Mock achievements data
  const mockAchievements = [
    {
      id: 'achievement-1',
      title: 'Songwriting Fundamentals',
      completedDate: new Date(2023, 5, 12) // June 12, 2023
    },
    {
      id: 'achievement-2',
      title: 'Mixing Essentials',
      completedDate: new Date(2023, 4, 3) // May 3, 2023
    },
    {
      id: 'achievement-3',
      title: 'Music Theory Basics',
      completedDate: new Date(2023, 3, 15) // April 15, 2023
    }
  ];

  // Calculate total XP earned
  const totalXp = mockEnrolledCourses.reduce((total, enrolled) => {
    const course = mockCourses.find(c => c.id === enrolled.courseId);
    return total + (course?.xpReward || 0) * (enrolled.progress / 100);
  }, 0);

  // Handler functions
  const handleContinueCourse = (courseId: string, moduleId: string) => {
    console.log(`Continuing course ${courseId} at module ${moduleId}`);
  };

  const handleEnrollCourse = (courseId: string) => {
    console.log(`Enrolling in course ${courseId}`);
  };

  return (
    <Layout>
      <div className="container py-8 space-y-6">
        <LearningHeader 
          enrolledCoursesCount={mockEnrolledCourses.length} 
          totalXp={Math.round(totalXp)} 
        />
        
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-4">
            <TabsTrigger value="my-learning">My Learning</TabsTrigger>
            <TabsTrigger value="courses">All Courses</TabsTrigger>
            <TabsTrigger value="news">Industry News</TabsTrigger>
            <TabsTrigger value="certifications">Certifications</TabsTrigger>
          </TabsList>
          
          <TabsContent value="my-learning">
            <MyLearningTabContent 
              courses={mockCourses}
              enrolledCourses={mockEnrolledCourses}
              achievements={mockAchievements}
              onContinueCourse={handleContinueCourse}
              onEnrollCourse={handleEnrollCourse}
            />
          </TabsContent>
          
          <TabsContent value="courses">
            <CoursesTabContent 
              courses={mockCourses}
              enrolledCourses={mockEnrolledCourses}
              onEnrollCourse={handleEnrollCourse}
              onContinueCourse={handleContinueCourse}
            />
          </TabsContent>
          
          <TabsContent value="news">
            <NewsTabContent />
          </TabsContent>
          
          <TabsContent value="certifications">
            <CertificationsTabContent />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Learning;
